/**
 * VOICE-C1 F5 — Adaptive barge-in telemetry.
 *
 * Stores the last `MAX_EVENTS` barge-in events in localStorage so we can
 * detect a high false-positive rate (user environment is noisy, threshold
 * fires without an actual interruption) and bump the threshold to be more
 * conservative. The bump is applied at read time via
 * getAdjustedBargeInThreshold(baseRms).
 *
 * False-positive heuristic:
 *   followed_by_user_speech === false  → false positive.
 *   The caller marks the event after the post-bargein listening window
 *   either commits a transcript (true) or expires (false).
 *
 * Adjustment policy:
 *   if false_positives >= ADJ_THRESHOLD over the rolling window,
 *   multiply baseRms by ADJ_FACTOR (1.3) → harder to trigger.
 *   Cap at ADJ_MAX_FACTOR (1.6) so an extremely noisy user does not
 *   silence barge-in entirely.
 *
 * Reset:
 *   Events older than RESET_DAYS are dropped on read, so a noisy hotel
 *   night does not penalise barge-in for the next month.
 *
 * Storage shape:
 *   localStorage["voice_telemetry"] = JSON.stringify({
 *     events: [{ timestamp, threshold_used, followed_by_user_speech }, ...]
 *   })
 *
 * Failures are swallowed (private mode, quota exceeded) — telemetry is
 * a soft optimisation; the base threshold must always work standalone.
 */

const STORAGE_KEY = "voice_telemetry";
export const MAX_EVENTS = 30;
export const ADJ_THRESHOLD = 5; // false positives needed to bump
export const ADJ_FACTOR = 1.3;
export const ADJ_MAX_FACTOR = 1.6;
export const RESET_DAYS = 30;

const _hasLocalStorage = () => {
  try {
    return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
  } catch (_) {
    return false;
  }
};

const _readEvents = () => {
  if (!_hasLocalStorage()) return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.events)) return [];
    const cutoff = Date.now() - RESET_DAYS * 24 * 60 * 60 * 1000;
    return parsed.events.filter((e) => e && typeof e.timestamp === "number" && e.timestamp >= cutoff);
  } catch (_) {
    return [];
  }
};

const _writeEvents = (events) => {
  if (!_hasLocalStorage()) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ events }));
  } catch (_) { /* quota / private mode → silent */ }
};

/**
 * Record a barge-in event. `followed_by_user_speech` may be null when the
 * event has just been recorded and the verdict is not in yet — call
 * `markLastEventFollowUp(true|false)` once the listening window resolves.
 */
export function recordBargeInEvent({ thresholdUsed, followedByUserSpeech = null }) {
  const events = _readEvents();
  events.push({
    timestamp: Date.now(),
    threshold_used: Number(thresholdUsed) || 0,
    followed_by_user_speech: followedByUserSpeech,
  });
  while (events.length > MAX_EVENTS) events.shift();
  _writeEvents(events);
  return events.length;
}

/**
 * Update the most recent event's verdict. Used after the listening window
 * post barge-in resolves with either a real transcript or a timeout.
 */
export function markLastEventFollowUp(followed) {
  const events = _readEvents();
  if (!events.length) return false;
  const last = events[events.length - 1];
  // Only mark if it has not been resolved yet, to avoid overriding history
  // when multiple barge-ins fire in quick succession.
  if (last.followed_by_user_speech === null) {
    last.followed_by_user_speech = !!followed;
    _writeEvents(events);
    return true;
  }
  return false;
}

/**
 * Count false positives over the rolling window. `null` (unresolved) is
 * ignored — only confirmed verdicts count.
 */
export function countFalsePositives() {
  const events = _readEvents();
  return events.filter((e) => e.followed_by_user_speech === false).length;
}

/**
 * Given a base RMS threshold (e.g. 0.050), return the adjusted threshold
 * factoring in false-positive history. Same input → same output until
 * the rolling window changes.
 */
export function getAdjustedBargeInThreshold(baseRms) {
  const base = Number(baseRms);
  if (!Number.isFinite(base) || base <= 0) return baseRms;
  const fp = countFalsePositives();
  if (fp < ADJ_THRESHOLD) return base;
  // Linearly extrapolate factor between ADJ_FACTOR (at threshold) and
  // ADJ_MAX_FACTOR (at MAX_EVENTS, i.e. all false positives).
  const excess = Math.max(0, fp - ADJ_THRESHOLD);
  const slope = (ADJ_MAX_FACTOR - ADJ_FACTOR) / (MAX_EVENTS - ADJ_THRESHOLD);
  const factor = Math.min(ADJ_MAX_FACTOR, ADJ_FACTOR + excess * slope);
  return base * factor;
}

/** Test-only — wipe storage. Production callers never use this. */
export function _resetTelemetryForTests() {
  if (_hasLocalStorage()) {
    try { window.localStorage.removeItem(STORAGE_KEY); } catch (_) {}
  }
}
